import { BodyText, Callout, HeaderText } from "@/components/ui";
import { useParams } from "@tanstack/react-router";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { LabTestCollapsible } from "./LabTestCollapsible";
import { LabsSkeleton } from "./LabsSkeleton";
import { useLabTests } from "./network/useLabTests";
import { useOrderResultHistoryAvailableActions } from "./network/useOrderHistoryAvailableActions";

export const Labs = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const {
    data: labTests,
    refetch,
    isPending: isLoadingLabTests,
    isError: isErrorLabTests,
  } = useLabTests(patientId);
  const {
    isPending: isLoadingAvailableActions,
    isError: isErrorAvailableActions,
  } = useOrderResultHistoryAvailableActions(patientId);

  const isLabTestsEmpty =
    !isLoadingLabTests &&
    !isErrorLabTests &&
    !labTests?.completed.length &&
    !labTests?.needsManualResulting.length &&
    !labTests?.needsTriagePartial.length &&
    !labTests?.needsTriage.length &&
    !labTests?.needsDiscussion.length &&
    !labTests?.needsSubmission.length &&
    !labTests?.ordered.length &&
    !labTests?.pending.length &&
    !labTests?.failed.length;

  const hasCompletedLabs = Boolean(labTests?.completed.length);
  const hasFailedLabs = Boolean(labTests?.failed.length);
  const hasPendingLabs = Boolean(
    labTests?.needsManualResulting.length ||
      labTests?.needsTriagePartial.length ||
      labTests?.needsTriage.length ||
      labTests?.needsDiscussion.length ||
      labTests?.needsSubmission.length ||
      labTests?.pending.length ||
      labTests?.ordered.length,
  );

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader title="Labs" />
      <SectionContent>
        {(isLoadingLabTests || isLoadingAvailableActions || !patientId) && (
          <LabsSkeleton />
        )}
        {isErrorLabTests ||
          (isErrorAvailableActions && (
            <div className="flex flex-col gap-2 py-2 w-full">
              <Callout
                className="w-full cursor-pointer"
                variant="danger"
                title="Unable to load labs data!"
                description="Please click here to try again."
                onClick={() => refetch()}
              />
            </div>
          ))}
        {!labTests ||
          (isLabTestsEmpty && (
            <div className="gap-1 mt-1 flex flex-row items-center">
              <BodyText size="sm" textColor="tertiary">
                No labs have been ordered
              </BodyText>
            </div>
          ))}
        {labTests && !isLabTestsEmpty && (
          <div className="flex flex-wrap flex-col w-full gap-2 mt-2">
            {hasFailedLabs && (
              <>
                <HeaderText size="sm" textColor="error">
                  Failed to Order
                </HeaderText>
                {labTests.failed.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
              </>
            )}
            {hasCompletedLabs && (
              <>
                <HeaderText size="sm" textColor="primary">
                  Completed
                </HeaderText>
                {labTests.completed.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
              </>
            )}
            {hasPendingLabs && (
              <>
                <HeaderText size="sm" textColor="warning">
                  Pending
                </HeaderText>
                {labTests.needsSubmission.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
                {labTests.needsDiscussion.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
                {labTests.needsTriage.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
                {labTests.needsTriagePartial.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
                {labTests.needsManualResulting.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
                {labTests.ordered.map((lab) => (
                  <LabTestCollapsible
                    key={lab.serviceRequest.id}
                    lab={lab}
                    patientId={patientId as string}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </SectionContent>
    </div>
  );
};
